import React, { createContext, useContext, useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, onSnapshot, query, where, orderBy } from "firebase/firestore";
import { useAuth } from "./AuthProvider";

// メニュー項目（ロールに基づく項目）の型
export interface NavigationMenuType {
  id: string;
  path: string;
  icon: string;
  label: string;
  permission: string;
  sort_no: number;
  child?: NavigationMenuType[];
}

// ユーザー個別の権限データの型
interface UserPermissionType {
  id: string;
  user_id: string;
  permissions: string[];
}

// Context の型定義
interface PermissionContextProps {
  permissionRolesData: NavigationMenuType[];
  permissionData: string[];
  loading: boolean;
}

const PermissionContext = createContext<PermissionContextProps>({
  permissionRolesData: [],
  permissionData: [],
  loading: true,
});

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [permissionRolesData, setPermissionRolesData] = useState<NavigationMenuType[]>([]);
  const [permissionData, setPermissionData] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  // 権限を取り終えたメール。ログイン直後は user が無い状態で一度 loading=false になり、メールが決まった直後の描画では
  // まだ前の (空の) 権限が見えてしまう。effect で loading に戻しても間に合わない (子の effect が先に走り、一覧が「個人」の
  // 範囲で先に取られてページが 1 に戻る。再読み込みで再現) ため、「このメールの権限を取り終えたか」を描画時に判定する
  const [loadedEmail, setLoadedEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.email) {
      setPermissionRolesData([]);
      setPermissionData([]);
      setLoading(false);
      return;
    }

    const email = user.email;
    setLoading(true);
    let rolesLoaded = false;
    let permissionsLoaded = false;
    const done = () => { if (rolesLoaded && permissionsLoaded) { setLoading(false); setLoadedEmail(email); } };

    // ロールベースのメニュー項目を取得
    const permissionRolesSnapshot = onSnapshot(
      query(
        collection(db, "HozenallCloud_Roles_Permissions"),
        orderBy("sort_no", "asc")
      ),
      (snapshot) => {
        const permissions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as NavigationMenuType));
        setPermissionRolesData(permissions);
        rolesLoaded = true;
        done();
      },
      (error) => {
        console.error("Hozenall Cloud Roles Permissions Firestore error:", error);
      }
    );

    // ユーザー個別の権限データを取得
    const permissionDataSnapshot = onSnapshot(
      query(
        collection(db, "HozenallCloud_Permissions"),
        where("user_id", "==", user.email)
      ),
      (snapshot) => {
        const permissions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as UserPermissionType));

        if (permissions.length > 0) {
          setPermissionData(permissions[0].permissions);
        } else {
          setPermissionData([]);
        }
        permissionsLoaded = true;
        done();
      },
      (error) => {
        console.error("Hozenall Cloud Permissions Firestore error:", error);
      }
    );

    return () => {
      permissionRolesSnapshot();
      permissionDataSnapshot();
    };
  }, [user?.email]);

  return (
    <PermissionContext.Provider value={{ permissionRolesData, permissionData, loading: loading || (!!user?.email && loadedEmail !== user.email) }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);
